import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { Search } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { type PastRun, RUN_GROUPS, type RunGroup } from '@atlas-control/shared';
import { z } from 'zod';
import { PageHeader } from '@/components/page-header';
import { Pagination } from '@/components/pagination';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { pastRunsQuery, readyFormsQuery, runCountsQuery, runsQuery } from '@/features/runs/api';
import { AtlasStatusBadge, RunStatusBadge, StageBar } from '@/features/runs/components';
import { formatDateTime, formatRelative } from '@/lib/format';
import { guard } from '@/lib/guards';
import { pageSearch } from '@/lib/page-search';

const Search_ = z.object({
  group: z.enum([...RUN_GROUPS, 'external']).default('all'),
  q: z.string().optional(),
  page: pageSearch,
});

export const Route = createFileRoute('/_app/runs/')({
  beforeLoad: ({ context }) => guard(context.me, 'runs.read'),
  validateSearch: Search_,
  component: RunsPage,
});

function RunsPage() {
  const { group, q, page = 1 } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const counts = useQuery(runCountsQuery);
  const external = useQuery(pastRunsQuery(undefined, 1));
  const [text, setText] = useState(q ?? '');
  const setPage = useCallback(
    (n: number) => void navigate({ search: (p) => ({ ...p, page: n > 1 ? n : undefined }) }),
    [navigate],
  );

  useEffect(() => {
    const t = setTimeout(() => {
      if ((q ?? '') !== text)
        void navigate({ search: (p) => ({ ...p, q: text || undefined, page: undefined }) });
    }, 300);
    return () => clearTimeout(t);
  }, [text, q, navigate]);

  return (
    <>
      <PageHeader title="Runs" />
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <Tabs
          value={group}
          onValueChange={(g) =>
            void navigate({ search: (p) => ({ ...p, group: g as typeof group, page: undefined }) })
          }
        >
          <TabsList>
            {TABS.map(([value, label]) => (
              <TabsTrigger key={value} value={value}>
                {label} {counts.data && <Count n={counts.data[value]} />}
              </TabsTrigger>
            ))}
            <TabsTrigger value="external">
              External runs {external.data && <Count n={external.data.total} />}
            </TabsTrigger>
          </TabsList>
        </Tabs>
        <div className="relative w-full sm:w-64">
          <Search className="absolute top-2.5 left-2.5 size-4 text-muted-foreground" aria-hidden />
          <Input
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Company, email, Atlas id"
            className="pl-8"
            aria-label="Search runs"
          />
        </div>
      </div>
      {group === 'to_start' ? (
        <ReadyTable q={q} page={page} setPage={setPage} />
      ) : group === 'external' ? (
        <ExternalRunsTab q={q} page={page} setPage={setPage} />
      ) : (
        <>
          <RunsTable group={group} q={q} page={page} setPage={setPage} />
          <ExternalRunsSection group={group} q={q} />
        </>
      )}
    </>
  );
}

const TABS: [RunGroup, string][] = [
  ['all', 'All'],
  ['to_start', 'To be started'],
  ['in_progress', 'In progress'],
  ['completed', 'Completed'],
  ['cancelled', 'Cancelled'],
];

const EMPTY: Record<Exclude<RunGroup, 'to_start'>, string> = {
  all: 'No runs yet.',
  in_progress: 'No runs in progress.',
  completed: 'No finished runs yet.',
  cancelled: 'No cancelled or failed runs.',
};

function Count({ n }: { n: number }) {
  return <span className="ml-1 rounded-full bg-muted px-1.5 text-xs tabular-nums">{n}</span>;
}

interface Paged {
  q?: string;
  page: number;
  setPage: (page: number) => void;
}

function ReadyTable({ q, page, setPage }: Paged) {
  const ready = useQuery(readyFormsQuery(q, page));
  const navigate = useNavigate();
  return (
    <>
      <Card className="py-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Company</TableHead>
              <TableHead>Contact</TableHead>
              <TableHead>Intake reviewed</TableHead>
              <TableHead>Received</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            <LoadingRows show={ready.isPending} cols={4} />
            {ready.data?.forms.length === 0 && (
              <EmptyRow
                cols={4}
                text="Nothing to start. Forms appear here once they are paid and their intake is reviewed."
              />
            )}
            {ready.data?.forms.map((f) => (
              <TableRow
                key={f.formId}
                className="cursor-pointer"
                onClick={() =>
                  void navigate({ to: '/forms/$formId', params: { formId: f.formId } })
                }
              >
                <TableCell className="font-medium">
                  <Link
                    to="/forms/$formId"
                    params={{ formId: f.formId }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {f.company}
                  </Link>
                </TableCell>
                <TableCell className="text-muted-foreground">{f.contactEmail ?? '-'}</TableCell>
                <TableCell title={formatDateTime(f.intakeReviewedAt)}>
                  {formatRelative(f.intakeReviewedAt)}
                </TableCell>
                <TableCell className="text-muted-foreground">
                  {formatRelative(f.receivedAt)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
      <Pagination info={ready.data} onPageChange={setPage} />
    </>
  );
}

function RunsTable({ group, q, page, setPage }: Paged & { group: Exclude<RunGroup, 'to_start'> }) {
  const list = useQuery(runsQuery(group, q, page));
  const navigate = useNavigate();
  return (
    <>
      <Card className="py-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Company</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Stage reached</TableHead>
              <TableHead>Waiting on</TableHead>
              <TableHead>Started</TableHead>
              <TableHead>Updated</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            <LoadingRows show={list.isPending} cols={6} />
            {list.data?.runs.length === 0 && <EmptyRow cols={6} text={EMPTY[group]} />}
            {list.data?.runs.map((r) => (
              <TableRow
                key={r.id}
                className="cursor-pointer"
                onClick={() => void navigate({ to: '/runs/$runId', params: { runId: r.id } })}
              >
                <TableCell className="font-medium">
                  <Link
                    to="/runs/$runId"
                    params={{ runId: r.id }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {r.company}
                  </Link>
                  {r.contactEmail && (
                    <div className="text-xs text-muted-foreground">{r.contactEmail}</div>
                  )}
                </TableCell>
                <TableCell>
                  <RunStatusBadge status={r.status} />
                </TableCell>
                <TableCell>
                  <StageBar
                    done={r.stagesDone}
                    total={r.stagesTotal}
                    reached={r.stageReached?.name ?? null}
                  />
                </TableCell>
                <TableCell>
                  {r.waitingOn ?? <span className="text-muted-foreground">-</span>}
                </TableCell>
                <TableCell className="text-muted-foreground" title={formatDateTime(r.startedAt)}>
                  {formatRelative(r.startedAt)}
                </TableCell>
                <TableCell className="text-muted-foreground">
                  {formatRelative(r.updatedAt)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
      <Pagination info={list.data} onPageChange={setPage} />
    </>
  );
}

function LoadingRows({ show, cols }: { show: boolean; cols: number }) {
  if (!show) return null;
  return Array.from({ length: 4 }, (_, i) => (
    <TableRow key={i}>
      <TableCell colSpan={cols}>
        <Skeleton className="h-5" />
      </TableCell>
    </TableRow>
  ));
}

function EmptyRow({ cols, text }: { cols: number; text: string }) {
  return (
    <TableRow>
      <TableCell colSpan={cols} className="py-10 text-center text-muted-foreground">
        {text}
      </TableCell>
    </TableRow>
  );
}

const READ_ONLY = 'Read only: they cannot be reviewed, continued or emailed from here.';

/**
 * The external runs that belong on this tab, under the panel's own runs.
 * Hidden when there are none.
 */
function ExternalRunsSection({ group, q }: { group: Exclude<RunGroup, 'to_start'>; q?: string }) {
  const list = useQuery(pastRunsQuery(q, 1, group === 'all' ? undefined : group, 100));
  if (!list.data?.runs.length) return null;
  return (
    <section className="mt-8">
      <h2 className="mb-1 text-base font-semibold">External runs</h2>
      <p className="mb-3 text-sm text-muted-foreground">
        Started in Atlas, not from the panel. {READ_ONLY}
      </p>
      <ExternalRunsCard runs={list.data.runs} pending={false} />
      {list.data.total > list.data.runs.length && (
        <p className="mt-2 text-sm text-muted-foreground">
          Showing {list.data.runs.length} of {list.data.total}. The External runs tab has them all.
        </p>
      )}
    </section>
  );
}

/** Every run Atlas has that the panel did not start. */
function ExternalRunsTab({ q, page, setPage }: Paged) {
  const list = useQuery(pastRunsQuery(q, page));
  return (
    <>
      <p className="mb-3 text-sm text-muted-foreground">
        Runs started in Atlas, not from the panel, including those from before the panel existed.{' '}
        {READ_ONLY}
        {list.data?.namesPending ? ` Looking up ${list.data.namesPending} company names…` : ''}
      </p>
      <ExternalRunsCard runs={list.data?.runs} pending={list.isPending} />
      <Pagination info={list.data} onPageChange={setPage} />
    </>
  );
}

function ExternalRunsCard({ runs, pending }: { runs: PastRun[] | undefined; pending: boolean }) {
  const navigate = useNavigate();
  return (
    <Card className="py-0">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Company</TableHead>
            <TableHead>Atlas status</TableHead>
            <TableHead>Blueprint</TableHead>
            <TableHead>Started</TableHead>
            <TableHead>Finished</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          <LoadingRows show={pending} cols={5} />
          {runs?.length === 0 && <EmptyRow cols={5} text="No external runs." />}
          {runs?.map((r) => (
            <TableRow
              key={r.atlasRunId}
              className="cursor-pointer"
              onClick={() =>
                void navigate({
                  to: '/past-runs/$atlasRunId',
                  params: { atlasRunId: r.atlasRunId },
                })
              }
            >
              <TableCell className="font-medium">
                <Link
                  to="/past-runs/$atlasRunId"
                  params={{ atlasRunId: r.atlasRunId }}
                  onClick={(e) => e.stopPropagation()}
                >
                  {r.company || <span className="text-muted-foreground">Name not recorded</span>}
                </Link>
                <div className="font-mono text-xs text-muted-foreground">
                  {r.atlasRunId.slice(0, 8)}
                </div>
              </TableCell>
              <TableCell>
                <AtlasStatusBadge status={r.status} />
              </TableCell>
              <TableCell>{r.hasBlueprint ? 'Yes' : '-'}</TableCell>
              <TableCell className="text-muted-foreground" title={formatDateTime(r.createdAt)}>
                {formatRelative(r.createdAt)}
              </TableCell>
              <TableCell className="text-muted-foreground">
                {r.completedAt ? formatRelative(r.completedAt) : '-'}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );
}
